/**
 * Decoder for VAF's opaque `hp` param.
 *
 * `redirectTo` rides wrapped in one base64url string instead of its own query param.
 * Base64url's alphabet (`[A-Za-z0-9_-]`) has no character a decode step can act on, so an
 * extra decode anywhere along the email's delivery path can't split it apart the way an
 * ordinary `?redirectTo=...&x=y` query string can. Must stay byte-compatible with
 * vercel-auth-functions `lib/handoffPayload.ts` — same param name, same JSON shape.
 */

/** The query parameter the opaque payload rides in. Same name VAF writes it under. */
export const HANDOFF_PAYLOAD_PARAM = 'hp';

export interface HandoffPayload {
  redirectTo?: string;
}

/**
 * Decodes the opaque handoff param, or returns `null` when absent or malformed.
 *
 * Malformed reads the same as absent — there's no error boundary here, so every caller
 * treats "nothing usable" as one case rather than distinguishing why.
 */
export function decodeHandoffPayload(raw: string | null | undefined): HandoffPayload | null {
  if (!raw) {
    return null;
  }
  try {
    const base64 = raw.replace(/-/g, '+').replace(/_/g, '/');
    const parsed = JSON.parse(atob(base64));
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }
    const { redirectTo } = parsed as Record<string, unknown>;
    return typeof redirectTo === 'string' && redirectTo ? { redirectTo } : null;
  } catch {
    return null;
  }
}

/**
 * Restores `redirectTo` from the opaque `hp` param onto plain `URLSearchParams`, in place.
 *
 * Every reader of a VAF magic-link callback URL — one per product — needs this before it
 * reads `params.get('redirectTo')`, or it silently sees nothing whenever the caller
 * supplied an address. Call once, then read `redirectTo` normally.
 */
export function applyHandoffPayload(params: URLSearchParams): void {
  const payload = decodeHandoffPayload(params.get(HANDOFF_PAYLOAD_PARAM));
  if (payload?.redirectTo) {
    params.set('redirectTo', payload.redirectTo);
  }
}
